import {useCallback, useEffect, useState} from 'react';

import {useMapsLibrary} from '@vis.gl/react-google-maps';

import {createLogger} from '~/shared/logging';

const logger = createLogger('useGoogleMapsAutocomplete');

const useGoogleMapsAutocomplete = (
  inputValue: string,
  debounceTimeout: number = 300,
) => {
  const [predictions, setPredictions] = useState<google.maps.places.AutocompleteSuggestion['placePrediction'][]>([]);
  const placesLibrary = useMapsLibrary('places');
  const geocodingLib = useMapsLibrary('geocoding');

  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);

  const geocodeByAddress = useCallback(
    async (address: string): Promise<google.maps.GeocoderResult[]> => {
      if (!geocoder) {
        throw new Error('Google Maps Geocoding library is not loaded yet.');
      }

      try {
        const response = await geocoder.geocode({address});
        return response.results;
      } catch (error) {
        logger.error('Geocoding error:', error);
        return [];
      }
    },
    [geocoder],
  );

  useEffect(() => {
    if (!geocodingLib) {
      return;
    }
    setGeocoder(new geocodingLib.Geocoder());
  }, [geocodingLib]);

  useEffect(() => {
    const handler = setTimeout(async () => {
      if (!placesLibrary || !inputValue) {
        setPredictions([]);
        return;
      }

      const request: google.maps.places.AutocompleteRequest = {
        input: inputValue,
        includedPrimaryTypes: ['geocode', 'establishment'],
        includedRegionCodes: ['ps', 'il'],
      };

      const res = await placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);

      setPredictions(res.suggestions.map(suggestion => {
        const {placePrediction} = suggestion;
        return placePrediction;
      }));
    }, debounceTimeout);

    return () => clearTimeout(handler);
  }, [inputValue, debounceTimeout, placesLibrary]);

  return {
    predictions,
    geocodeByAddress,
  };
};

export default useGoogleMapsAutocomplete;
